/**
 * 1.36 — a size exchange at the till („გაცვლა").
 *
 * `create_exchange` (migration 157) writes every number; this file is the
 * screen's copy of its rules, held to the spec's examples
 * (docs/superpowers/specs/2026-10-11-size-exchange-design.md §5) by
 * exchange.test.ts. Change one, change both.
 *
 * The value of an exchanged unit lives ON THE LINE: a paired new line is sold
 * at the credited value (unit_price = total/qty), never as a sale-wide
 * discount — `netRatio` would spread that over every line of the new sale on
 * the NEXT exchange (Codex's Critical #1).
 */
import type { CardProvider, PaymentMethod, Sale } from './mock-data'
import { isMeasureLine } from './weight'

/**
 * 1.75 — a weighed line stays out of the exchange: its window and its receipt count whole
 * units („−" and „+", one at a time), so 0.4 kg left could not be picked right and a new
 * weighed line could only be whole kilos. The owner's return takes a weight to the gram.
 */
export const weighedExchange = (unit: string | null | undefined, left: number): boolean => isMeasureLine(unit, left, left)

/** Tetri, rounded half away from zero like Postgres's round(numeric, 2). */
export function tetri(n: number): number {
  return (Math.sign(n) * Math.round(Math.abs(n) * 100 + 1e-7)) / 100
}

export const EXCHANGE_DAYS_DEFAULT = 14

/** The owner's window (settings `exchangeDays`): 0–365, anything else is 14 — as `private.exchange_days`. */
export function parseExchangeDays(raw: string | null | undefined): number {
  return raw != null && /^\d{1,3}$/.test(raw) && Number(raw) <= 365 ? Number(raw) : EXCHANGE_DAYS_DEFAULT
}

// ── what exchange_candidates answers ────────────────────────────────────────

export type ExchangeLine = {
  sale_item_id: string
  product_id: string
  product_name: string
  model_id: string | null
  size: string | null
  color: string | null
  /** the price the line was sold at (an exchange's paired line: the credited value) */
  unit_price: number
  /** the product's retail price TODAY — what „same price" compares (a paired line's
   *  unit_price is its credited value, so the chain needs today's list price) */
  list_price: number
  quantity: number
  /** units of this product still returnable on the sale */
  left: number
  /** what one returned unit is credited — the server's number */
  credited_unit: number
  blocked: 'serial' | null
}

export type ExchangeCandidate = {
  id: string
  receipt: string
  created_at: string
  bought_at: string
  in_window: boolean
  branch_name: string | null
  cashier_name: string | null
  customer_name: string | null
  payment_method: PaymentMethod
  paid_cash: number
  paid_card: number
  card_provider: CardProvider | null
  total: number
  refunded: number
  blocked_sale: 'credit' | 'bonus' | null
  lines: ExchangeLine[]
}

export type CandidatePage = { days: number; rows: ExchangeCandidate[]; more: boolean }

const num = (v: unknown) => (v == null || v === '' ? 0 : Number(v))
const str = (v: unknown) => (v == null ? null : String(v))

export function mapCandidatePage(raw: unknown): CandidatePage {
  const r = (raw ?? {}) as Record<string, unknown>
  const rows = Array.isArray(r.rows) ? (r.rows as Record<string, unknown>[]) : []
  return {
    days: parseExchangeDays(r.days == null ? null : String(r.days)),
    more: r.more === true,
    rows: rows.map((s) => ({
      id: String(s.id),
      receipt: String(s.receipt ?? ''),
      created_at: String(s.created_at),
      bought_at: String(s.bought_at ?? s.created_at),
      in_window: s.in_window === true,
      branch_name: str(s.branch_name),
      cashier_name: str(s.cashier_name),
      customer_name: str(s.customer_name),
      payment_method: String(s.payment_method) as PaymentMethod,
      paid_cash: num(s.paid_cash),
      paid_card: num(s.paid_card),
      card_provider: str(s.card_provider) as CardProvider | null,
      total: num(s.total),
      refunded: num(s.refunded),
      blocked_sale: s.blocked_sale === 'credit' || s.blocked_sale === 'bonus' ? s.blocked_sale : null,
      lines: (Array.isArray(s.lines) ? (s.lines as Record<string, unknown>[]) : []).map((l) => ({
        sale_item_id: String(l.sale_item_id),
        product_id: String(l.product_id),
        product_name: String(l.product_name ?? ''),
        model_id: str(l.model_id),
        size: str(l.size),
        color: str(l.color),
        unit_price: num(l.unit_price),
        list_price: num(l.list_price),
        quantity: num(l.quantity),
        left: num(l.left),
        credited_unit: num(l.credited_unit),
        blocked: l.blocked === 'serial' ? 'serial' : null,
      })),
    })),
  }
}

// ── pairing and totals ──────────────────────────────────────────────────────

/** A returned line, as the dialog picked it. */
export type BackPick = {
  sale_item_id: string
  product_id: string
  model_id: string | null
  /** the returned product's retail price today */
  list_price: number
  credited_unit: number
  quantity: number
}

/** A new product, as the dialog picked it (promotion already applied by the till's rule). */
export type NewPick = {
  product_id: string
  model_id: string | null
  sale_price: number
  quantity: number
  /** the returned line this swaps for at ₾0, or null */
  paired_item: string | null
  promotion_id: string | null
  promo_discount: number
}

export type ExchangeLineOut = {
  product_id: string
  quantity: number
  unit_price: number
  total_price: number
  paired_item: string | null
  promotion_id: string | null
  promo_discount: number
}

export type ExchangeTotals = {
  credited: number
  newTotal: number
  difference: number
  lines: ExchangeLineOut[]
  problem: 'empty' | 'duplicate' | 'pair' | 'cheaper' | null
}

/**
 * Same model (or the same product), both at the same retail price TODAY → a ₾0
 * swap. Today's prices, not the line's: a paired line of an earlier exchange
 * carries its credited value, and the second size swap of the chain must still
 * be ₾0 (and a model repriced since the purchase still swaps sizes for nothing).
 */
export function canPair(
  b: Pick<BackPick, 'product_id' | 'model_id' | 'list_price'>,
  n: Pick<NewPick, 'product_id' | 'model_id' | 'sale_price'>,
): boolean {
  if (Math.abs(n.sale_price - b.list_price) > 0.005) return false
  return n.product_id === b.product_id || (!!n.model_id && n.model_id === b.model_id)
}

/** §5: C (capped at what is still refundable), the new lines, T and Δ. */
export function exchangeTotals(back: BackPick[], next: NewPick[], refundable: number): ExchangeTotals {
  let problem: ExchangeTotals['problem'] = back.length === 0 || next.length === 0 ? 'empty' : null
  const raw = back.reduce((s, b) => s + tetri(b.quantity * b.credited_unit), 0)
  const credited = tetri(Math.min(raw, Math.max(0, refundable)))
  const used = new Map<string, number>()
  const seen = new Set<string>()
  const lines = next.map((n): ExchangeLineOut => {
    if (seen.has(n.product_id) && !problem) problem = 'duplicate'
    seen.add(n.product_id)
    if (n.paired_item) {
      const b = back.find((x) => x.sale_item_id === n.paired_item)
      const u = (used.get(n.paired_item) ?? 0) + n.quantity
      used.set(n.paired_item, u)
      if (b && canPair(b, n) && u <= b.quantity + 0.0005) {
        return {
          product_id: n.product_id, quantity: n.quantity, unit_price: b.credited_unit,
          total_price: tetri(n.quantity * b.credited_unit), paired_item: n.paired_item, promotion_id: null, promo_discount: 0,
        }
      }
      if (!problem) problem = 'pair'
    }
    const pd = tetri(n.promo_discount)
    return {
      product_id: n.product_id, quantity: n.quantity, unit_price: n.sale_price,
      total_price: tetri(n.sale_price * n.quantity - pd), paired_item: null,
      promotion_id: pd > 0 ? n.promotion_id : null, promo_discount: pd,
    }
  })
  const newTotal = tetri(lines.reduce((s, l) => s + l.total_price, 0))
  const difference = tetri(newTotal - credited)
  if (!problem && difference < 0) problem = 'cheaper'
  return { credited, newTotal, difference, lines, problem }
}

// ── rails ───────────────────────────────────────────────────────────────────

export type Rails = { cash: number; card: number }
type RailSale = Pick<Sale, 'total' | 'payment_method' | 'paid_cash' | 'paid_card'>

/** What a sale took on each rail; a row from before 103's split reads as its method. */
export function originalRails(s: RailSale): Rails {
  const cash = s.paid_cash ?? 0
  const card = s.paid_card ?? 0
  if (cash + card >= 0.005) return { cash, card }
  return s.payment_method === 'card' ? { cash: 0, card: s.total } : { cash: s.total, card: 0 }
}

/**
 * The rails of a return of `amount` against `original`, CUMULATIVE over its
 * earlier returns (Codex #4): the cash target of everything refunded so far,
 * minus the cash already handed back. The last return lands each rail exactly.
 */
export function returnRails(original: RailSale, prior: { total: number; paid_cash: number }[], amount: number): Rails {
  const o = originalRails(original)
  const share = o.cash + o.card > 0 ? o.cash / (o.cash + o.card) : 1
  const refunded = prior.reduce((s, r) => s + r.total, 0)
  const cashBack = prior.reduce((s, r) => s + (r.paid_cash ?? 0), 0)
  const cash = Math.min(Math.max(tetri(tetri((refunded + amount) * share) - cashBack), 0), amount)
  return { cash: tetri(cash), card: tetri(amount - cash) }
}

// ── the shift close line ────────────────────────────────────────────────────

export type ExchangeShiftTotals = { count: number; extra: number }

/** Per shift: complete pairs (both legs loaded) and what was paid on top. */
export function exchangesByShift(
  sales: { exchange_id?: string | null; type: string; total: number; shift_id?: string | null }[],
): Map<string, ExchangeShiftTotals> {
  const legs = new Map<string, { sale?: { total: number; shift_id?: string | null }; ret?: { total: number } }>()
  for (const s of sales) {
    if (!s.exchange_id) continue
    const l = legs.get(s.exchange_id) ?? {}
    if (s.type === 'return') l.ret = s
    else l.sale = s
    legs.set(s.exchange_id, l)
  }
  const out = new Map<string, ExchangeShiftTotals>()
  for (const { sale, ret } of legs.values()) {
    if (!sale || !ret || !sale.shift_id) continue
    const cur = out.get(sale.shift_id) ?? { count: 0, extra: 0 }
    out.set(sale.shift_id, { count: cur.count + 1, extra: tetri(cur.extra + sale.total - ret.total) })
  }
  return out
}

// ── the server's refusals ───────────────────────────────────────────────────

export type ExchangeErrorKey =
  | 'cheaper' | 'window' | 'pin' | 'pinLocked' | 'shift' | 'credit' | 'bonus' | 'fiscal'
  | 'serial' | 'stock' | 'price' | 'bank' | 'room' | 'generic'

export function exchangeErrorOf(message: string): { key: ExchangeErrorKey; params: Record<string, string> } {
  const m = message.trim()
  const parts = (code: string) => m.slice(m.indexOf(code) + code.length + 1).split(':')
  if (m.includes('EXCHANGE_CHEAPER')) {
    const [a, b] = parts('EXCHANGE_CHEAPER')
    return { key: 'cheaper', params: { a: a ?? '', b: b ?? '' } }
  }
  if (m.includes('EXCHANGE_WINDOW')) return { key: 'window', params: { days: parts('EXCHANGE_WINDOW')[0] ?? '' } }
  if (m.includes('EXCHANGE_PIN')) return { key: 'pin', params: {} }
  if (m.includes('PIN_LOCKED')) return { key: 'pinLocked', params: {} }
  if (m.includes('EXCHANGE_NEEDS_SHIFT') || m.includes('EXCHANGE_SHIFT_BRANCH')) return { key: 'shift', params: {} }
  if (m.includes('EXCHANGE_CREDIT_SALE')) return { key: 'credit', params: {} }
  if (m.includes('EXCHANGE_BONUS_SALE')) return { key: 'bonus', params: {} }
  if (m.includes('EXCHANGE_FISCAL')) return { key: 'fiscal', params: {} }
  if (m.includes('EXCHANGE_SERIAL_LINE')) return { key: 'serial', params: {} }
  if (m.includes('INSUFFICIENT_STOCK')) return { key: 'stock', params: { name: parts('INSUFFICIENT_STOCK')[0] ?? '' } }
  if (m.includes('PRICE_MISMATCH') || m.includes('EXCHANGE_PAIR') || m.includes('PROMO_')) return { key: 'price', params: {} }
  if (m.includes('EXCHANGE_CARD_PROVIDER')) return { key: 'bank', params: {} }
  if (m.includes('RETURN_EXCEEDS_SALE')) return { key: 'room', params: {} }
  return { key: 'generic', params: {} }
}

/** + what came back, − what was given, summed per product (a same-product swap nets 0). */
export function stockDelta(
  back: { product_id: string | null; quantity: number }[],
  given: { product_id: string | null; quantity: number }[],
): Map<string, number> {
  const d = new Map<string, number>()
  for (const l of back) if (l.product_id) d.set(l.product_id, (d.get(l.product_id) ?? 0) + l.quantity)
  for (const l of given) if (l.product_id) d.set(l.product_id, (d.get(l.product_id) ?? 0) - l.quantity)
  return d
}

// ── one attempt, one ref (Codex, build review #3) ──────────────────────────

/** The dialog's attempt: the client ref it sends and whether its picks are frozen. */
export type AttemptState = { ref: string | null; locked: boolean }

/** Sending freezes the picks AT ONCE and reuses the ref of an earlier lost reply. */
export function attemptStart(prev: AttemptState, newRef: () => string): AttemptState {
  return { ref: prev.ref ?? newRef(), locked: true }
}

/** A lost reply may have committed: keep the ref and the lock. Any definitive answer frees both. */
export function attemptAfter(prev: AttemptState, outcome: { ok: boolean; definitive?: boolean }): AttemptState {
  if (!outcome.ok && outcome.definitive === false) return { ref: prev.ref, locked: true }
  return { ref: null, locked: false }
}
